import { HEADER_HEIGHT } from "@/constants/components";
import { useActivePathCheck } from "@/hooks/useActivePathCheck";
import { useIsFromPrime } from "@/hooks/useIsFromPrime";

import {
  colors,
  HStack,
  TabBar,
  GoiLogo,
  Spacing,
  GLOBAL_MAX_WIDTH,
  NextLink,
  customEvent,
  EventTriggerAndTargetType,
  Hamburger,
  useResponsive,
  VStack,
  responsive,
  call,
  getPrimeUrl,
  COST_CALC_HEADER_URL,
  COST_CARD_ARM_EVENT,
  shouldHeaderGoToCostCalc,
} from "goi_common";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";

import { useQuery } from "react-query";

import { Axios } from "@/utils/axios";
import HeaderButton from "./HeaderButton";

export function Header({ onOpen }: { onOpen: () => void }) {
  const router = useRouter();
  const handleHamburgerClick = () => {
    customEvent({
      newGtm: {
        path: "gnb",
        trigger_and_target: "hamburger",
      },
    });
    onOpen();
  };

  const isHeaderTabBarHidden = useMemo(() => {
    return router.asPath.includes("service");
  }, [router]);

  const isServicePage = useMemo(() => {
    return (
      router.pathname === "/service" ||
      router.pathname.includes("/service/[type]") ||
      router.pathname.includes("/premium/service/[type]/")
    );
  }, [router]);

  const { isBiggerThanLg } = useResponsive();
  const isFromPrime = useIsFromPrime();

  return (
    <header className="goi-global-header">
      <div
        css={{
          position: "fixed",
          width: "100%",
          top: "0",
          zIndex: 1000,
          backgroundColor: colors.white,
          borderBottom: `1px solid ${colors.gray100}`,
          transition: "none",
          userSelect: "none",
        }}
      >
        <VStack
          margin="0 auto"
          css={responsive({
            width: {
              base: "100%",
              lg: `${GLOBAL_MAX_WIDTH}px`,
            },
          })}
        >
          <HStack height={HEADER_HEIGHT}>
            <HStack
              padding="13px 16px 13px 20px"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              {isFromPrime ? (
                <a
                  id="goi-logo"
                  href={getPrimeUrl("/")}
                  css={{ outline: "none", textDecoration: "none" }}
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "gnb",
                        trigger_and_target: "logo-uv_home",
                      },
                    });
                  }}
                >
                  <GoiLogo color={colors.goiBlack} size={30} />
                </a>
              ) : (
                <NextLink
                  id="goi-logo"
                  href="/"
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path: "gnb",
                        trigger_and_target: "logo-uv_home",
                      },
                    });
                  }}
                >
                  <GoiLogo color={colors.goiBlack} size={30} />
                </NextLink>
              )}
              <HStack alignItems="center">
                <div
                  css={responsive({
                    display: {
                      lg: "block",
                      base: "none",
                    },
                  })}
                >
                  <HeaderTabBar isFromPrime={isFromPrime} />
                </div>

                <Spacing orientation="horizontal" gap={36} />
                <HeaderButtons isHomeViewportTop={false} homePath="/" />

                <Spacing orientation="horizontal" gap={16} />
                <Box position="relative">
                  <button
                    onClick={handleHamburgerClick}
                    css={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Hamburger color={colors.gray900} size={24} />
                  </button>
                </Box>
              </HStack>
            </HStack>
          </HStack>

          {(isFromPrime || (!isServicePage && !isHeaderTabBarHidden)) && (
            <div
              css={responsive({
                display: {
                  lg: "none",
                  base: "block",
                },
              })}
            >
              <HeaderTabBar isFromPrime={isFromPrime} />
            </div>
          )}
        </VStack>
      </div>

      <Spacing gap={isBiggerThanLg ? HEADER_HEIGHT : HEADER_HEIGHT + 40} />
    </header>
  );
}

const PRIME_MENUS: {
  text: string;
  to: string;
  eventDetails: EventTriggerAndTargetType["gnb"][];
  activePath: string;
}[] = [
  {
    text: "읽을거리",
    to: getPrimeUrl("/content"),
    eventDetails: ["nav-uv_guidebook"],
    activePath: "content",
  },
  {
    text: "고객후기",
    to: "/review/",
    eventDetails: ["review-uv_review"],
    activePath: "후기",
  },
  {
    text: "100원상조",
    to: getPrimeUrl("/"),
    eventDetails: ["nav-uv_prime"],
    activePath: "",
  },
  {
    text: "장례견적",
    to: "/match/request/",
    eventDetails: ["nav-uv_match_request"],
    activePath: "견적",
  },
];

export const MENU_DATA = {
  견적: {
    to: "/match/request/",
    activePath: "견적",
  },
  상품안내: {
    to: "/product",
    activePath: "상품",
  },
  가이드북: {
    to: "/guidebook/",
    activePath: "가이드북",
  },
  후기: {
    to: "/review/",
    activePath: "후기",
  },
} as const;

const MENUS: {
  text: string;
  to: string;
  eventDetails: EventTriggerAndTargetType["gnb"][];
  activePath: string;
}[] = [
  // {
  //   text: "홈",
  //   to: "/",
  //   eventDetails: ["nav-uv_home"],
  // },
  {
    text: "장례견적",
    to: MENU_DATA["견적"].to,
    eventDetails: ["nav-uv_match_request"],
    activePath: MENU_DATA["견적"].activePath,
  },
  {
    text: "상품안내",
    to: MENU_DATA["상품안내"].to,
    eventDetails: ["nav-uv_prime"],
    activePath: MENU_DATA["상품안내"].activePath,
  },
  {
    text: "장례절차",
    to: MENU_DATA["가이드북"].to,
    eventDetails: ["nav-uv_guidebook"],
    activePath: MENU_DATA["가이드북"].activePath,
  },
  {
    text: "고객후기",
    to: MENU_DATA["후기"].to,
    eventDetails: ["review-uv_review"],
    activePath: MENU_DATA["후기"].activePath,
  },

  // {
  //   text: "이벤트",
  //   to: "https://prime.goifuneral.co.kr/promotion?utm_campaign=platform",
  //   eventDetails: ["nav-uv_prime_event"],
  // },
  // {
  //   text: "24시간 전화",
  //   to: "tel:1666-9784",
  //   eventDetails: ["call-opn_call_mdl"],
  // },
];

function HeaderTabBar({ isFromPrime }: { isFromPrime: boolean }) {
  const { activePath } = useActivePathCheck();
  const { isBiggerThanLg } = useResponsive();
  const { data, isSuccess } = useQuery(["funeral_reviews"], () => {
    return Axios.get("/sangjo_reviews/");
  });

  {
    /** 후기 안보일 경우 853개로 고정 */
  }
  const reviewCount = isSuccess ? data?.data?.count || 853 : 0;
  const menus = isFromPrime ? PRIME_MENUS : MENUS;
  const router = useRouter();
  /** 카드 실험(#361~#363) B 로 기록된 사람은 "장례견적"이 계산기로 간다. 배정은 하지 않고 기록만 읽는다 */
  const [quoteToCalc, setQuoteToCalc] = useState(false);
  useEffect(() => {
    // 처음 배정되는 페이지에서는 카드 게이트가 헤더보다 늦게 기록하므로 알림을 받아 다시 읽는다
    const sync = () => setQuoteToCalc(shouldHeaderGoToCostCalc());
    sync();
    window.addEventListener(COST_CARD_ARM_EVENT, sync);
    return () => window.removeEventListener(COST_CARD_ARM_EVENT, sync);
  }, [router.asPath]);

  return (
    <Box
      margin="0 10px"
      css={{
        overflow: "auto",
      }}
    >
      <TabBar
        gap={isBiggerThanLg ? 32 : 0}
        justifyContent="space-between"
        height="40px"
      >
        {menus.map((item, i) => {
          const isReview = item.to === "/review/";
          const isPrime = item.to.includes("product");
          const isExternalLink = item.to.startsWith("http");
          const isActive = isExternalLink
            ? false
            : activePath === item.activePath;
          const isQuoteToCalc =
            quoteToCalc && item.eventDetails.includes("nav-uv_match_request");

          const handleTabClick = () => {
            item.eventDetails.map((trigger_and_target) => {
              customEvent({ newGtm: { path: "gnb", trigger_and_target } });
            });
          };

          const tabContent = (
            <TabBar.Tab
              className={`${
                isActive
                  ? "subtitle_14_sb md_subtitle_18_sb"
                  : "body_14_m md_body_18_m"
              } gray500`}
              css={responsive({
                padding: "6px 0px",
                margin: "0 6px",
                whiteSpace: "nowrap",
                textAlign: "center",
                borderBottom: {
                  base: isActive ? `4px solid ${colors.gray900}` : "",
                  lg: "none",
                },
                color: { base: isActive ? colors.gray900 : "" },
              })}
              onClick={handleTabClick}
            >
              <HStack gap="2px" alignItems="center" justifyContent="center">
                {/* {isPrime && (
                      <Box ... >EVENT</Box>
                    )} */}
                {item.text}
                {isPrime && (
                  <Box
                    className="white"
                    padding="1px 6px"
                    backgroundColor={colors.orange700}
                    borderRadius="20px"
                    css={{
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: "500",
                      lineHeight: "18px",
                    }}
                  >
                    N
                  </Box>
                )}
                {isReview && reviewCount > 0 && (
                  <Box
                    className="white"
                    padding="1px 6px"
                    backgroundColor={colors.orange700}
                    borderRadius="20px"
                    css={{
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: "500",
                      lineHeight: "18px",
                    }}
                  >
                    {reviewCount}
                  </Box>
                )}
              </HStack>
            </TabBar.Tab>
          );

          return (
            <Box position="relative" key={i}>
              {isExternalLink || isQuoteToCalc ? (
                // 계산기는 platform 앱 페이지라 ssr 라우터가 아니라 문서 이동으로 간다
                <a
                  href={isQuoteToCalc ? COST_CALC_HEADER_URL : item.to}
                  css={{ outline: "none", textDecoration: "none" }}
                >
                  {tabContent}
                </a>
              ) : (
                <NextLink href={item.to}>{tabContent}</NextLink>
              )}
            </Box>
          );
        })}
      </TabBar>
    </Box>
  );
}

export function HeaderButtons({
  isHomeViewportTop,
  homePath,
}: {
  isHomeViewportTop: boolean;
  homePath: string;
}) {
  // const isLoggedIn = useRecoilValue(isLoggedInState);
  // const router = useRouter();

  return (
    <>
      {/* {isLoggedIn || (
        <div
          className="body3_m"
          css={{
            padding: "6px 6px",
            backgroundColor: isHomeViewportTop ? "transparent" : "white",
            color: isHomeViewportTop ? "white" : "black",
            border: `1px solid ${colors.gray200}`,
            borderRadius: "5px",
            marginRight: "8px",
            tansition: "all 0.1s linear",
          }}
          onClick={() => {
            customEvent({
              newGtm: {
                path: "gnb",
                trigger_and_target: "contract_check-prime",
              },
            });
            router.push(`https://prime.goifuneral.co.kr/?utm_campaign=platform`);
          }}
        >
          100원상조
        </div>
      )} */}
      {
        // isLoggedIn ? (
        //   <HeaderBtn
        //     type="BLACK"
        //     onClick={() => {
        //       router.push("/step-guide/initiation/");
        //     }}
        //     eventTrigger="step-guide"
        //   >
        //     장례준비 가이드
        //   </HeaderBtn>
        // ) :
        // <HeaderBtn
        //   type="ORANGE"
        //   onClick={() => {
        //     router.push("/funeral-prepare/1/");
        //   }}
        //   eventTrigger="srvc_rcmms-uv_funeral_prepare_1"
        // >
        //   장례가 처음이라면?
        // </HeaderBtn>

        <HeaderButton
          path="gnb"
          type="RED"
          onClick={() => {
            call("1666-9784");
            // router.push("/funeral-prepare/1/");
          }}
          eventTrigger="srvc_rcmms-uv_funeral_prepare_1"
        >
          24시간 고객센터
        </HeaderButton>
      }
    </>
  );
}

export const GOI_SERVICE_ALL_PACKAGES = [
  {
    name: "무빈소장",
    url: "/service/무빈소장/",
    imageSrc: "platform/service/service_mubinso.png",
    imageAlt: "무빈소장 품목",
  },
  {
    name: "가족장",
    url: "/service/가족장/",
    imageSrc: "platform/service/service_family.png",
    imageAlt: "가족장 품목",
  },
  {
    name: "가족장 프리미엄",
    url: "/promotion/service/가족장/premium-2025/",
    imageSrc: "platform/service/service_family_premium.png",
    imageAlt: "가족장 프리미엄 품목",
  },
  {
    name: "일반장",
    url: "/service/일반장/",
    imageSrc: "platform/service/service_normal.png",
    imageAlt: "일반장 품목",
  },
  {
    name: "일반장 프리미엄",
    url: "/promotion/service/일반장/premium-2025/",
    imageSrc: "platform/service/service_normal_premium.png",
    imageAlt: "일반장 프리미엄 품목",
  },
];

export const useHandleHeaderServiceTabBar = () => {
  const [isHide, setIsHide] = useState(false);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll: any = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 0) {
        setIsHide(false);
      } else {
        setIsHide(currentScrollY > lastScrollY);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return { isHide };
};
